import React, { useEffect, useState } from 'react';
import { Plus, Save, Loader2, CheckCircle2, Edit3, X, FileText, Download, Trash2 } from 'lucide-react';
import { api, uploadImage, resolveImage } from '../../lib/api';
import { Field, TextInput, NumberInput, Select, Button } from '../../components/admin/FormControls';

const CATEGORIES = [
  { value: 'policies', label: 'Policies & Strategies' },
  { value: 'reports', label: 'Reports & Publications' },
  { value: 'laws', label: 'Laws & Regulations' }
];

const empty = { title: '', category: 'policies', file_url: '', file_size: 0, order: 0 };

const FileField = ({ value, onChange, size, onSize }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setErr('');
    try {
      const res = await uploadImage(f); // same /api/uploads endpoint works for any allowed type incl. PDF
      onChange(res.url);
      onSize(res.size || f.size);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <label className="mb-1.5 block text-[12.5px] font-medium text-[#0B2545]">
        Document file (PDF, image)
      </label>
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-dashed border-[#E6DDC8] bg-[#FAF6EC] p-4">
        {value ? (
          <div className="flex flex-1 items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-white text-[#C9A86A]">
              <FileText size={18} />
            </div>
            <div className="min-w-0">
              <div className="truncate text-[13px] font-medium text-[#0B2545]">
                Uploaded — {(size / 1024).toFixed(1)} KB
              </div>
              <a href={resolveImage(value)} target="_blank" rel="noopener noreferrer" className="text-[12px] text-[#C9A86A] underline">
                Open file
              </a>
            </div>
            <button
              type="button"
              onClick={() => { onChange(''); onSize(0); }}
              className="rounded-md border border-red-200 px-2 py-1 text-[12px] text-red-600 hover:bg-red-50"
            >
              Clear
            </button>
          </div>
        ) : (
          <div className="flex-1 text-[12.5px] text-[#0B2545]/60">
            No file uploaded yet.
          </div>
        )}
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-[#E6DDC8] bg-white px-3 py-1.5 text-[12.5px] font-medium text-[#0B2545] hover:bg-[#F6F2EA]">
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
          {uploading ? 'Uploading...' : (value ? 'Replace' : 'Upload')}
          <input type="file" accept=".pdf,image/*" className="hidden" onChange={handle} />
        </label>
      </div>
      {err && <div className="mt-1 text-[12px] text-red-600">{err}</div>}
    </div>
  );
};

const AdminDocuments = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get('/documents');
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2200); };

  const startNew = () => setEditing({ ...empty, order: (items[items.length - 1]?.order || 0) + 1 });
  const startEdit = (n) => setEditing({ ...n });

  const save = async () => {
    if (!editing.title?.trim()) {
      flash('Title is required');
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        const { id, ...rest } = editing;
        await api.put(`/documents/${id}`, rest);
      } else {
        await api.post('/documents', editing);
      }
      flash('Saved');
      setEditing(null);
      load();
    } catch (e) {
      flash(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete document "${n.title}"?`)) return;
    await api.delete(`/documents/${n.id}`);
    flash('Deleted');
    load();
  };

  return (
    <div>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#0B2545]">Documents</h1>
          <p className="mt-1 text-[13.5px] text-[#0B2545]/65">
            Upload and manage PDFs, policies, reports and laws. Files are stored permanently in the database.
          </p>
        </div>
        <Button variant="gold" onClick={startNew}><Plus size={14} /> Add document</Button>
      </div>

      {msg && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-[13px] text-green-700">
          <CheckCircle2 size={14} /> {msg}
        </div>
      )}

      {loading ? (
        <div className="text-[#0B2545]/60"><Loader2 className="inline animate-spin" size={14} /> Loading...</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-[#E6DDC8] bg-white">
          <table className="w-full text-left text-[13.5px]">
            <thead className="bg-[#FAF6EC] text-[12px] uppercase tracking-wide text-[#0B2545]/65">
              <tr>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">File</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E6DDC8]">
              {items.map((n) => (
                <tr key={n.id} className="text-[#0B2545]">
                  <td className="px-4 py-3">{n.order}</td>
                  <td className="px-4 py-3 font-medium">{n.title}</td>
                  <td className="px-4 py-3 capitalize text-[#C9A86A]">{n.category}</td>
                  <td className="px-4 py-3 text-[#0B2545]/70">{n.file_size ? `${(n.file_size / 1024).toFixed(1)} KB` : '—'}</td>
                  <td className="px-4 py-3">
                    {n.file_url ? (
                      <a href={resolveImage(n.file_url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#C9A86A] hover:underline">
                        <Download size={12} /> Open
                      </a>
                    ) : <span className="text-[#0B2545]/40">No file</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="outline" onClick={() => startEdit(n)}><Edit3 size={13} /> Edit</Button>
                    <button onClick={() => remove(n)} className="ml-2 text-red-600 hover:underline"><Trash2 size={12} className="inline" /> Delete</button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-[#0B2545]/55">No documents yet. Click "Add document" to upload one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-xl rounded-lg bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[#E6DDC8] px-6 py-4">
              <h3 className="font-serif text-lg font-bold text-[#0B2545]">{editing.id ? 'Edit document' : 'New document'}</h3>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="space-y-4 p-6">
              <Field label="Title"><TextInput value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} /></Field>
              <Field label="Category">
                <Select value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} options={CATEGORIES} />
              </Field>
              <FileField
                value={editing.file_url}
                size={editing.file_size}
                onChange={(v) => setEditing({ ...editing, file_url: v })}
                onSize={(s) => setEditing({ ...editing, file_size: s })}
              />
              <Field label="Order"><NumberInput value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} /></Field>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-[#E6DDC8] px-6 py-4">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} disabled={saving}>{saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDocuments;
